import { useMemo } from 'react'
import { useGetInvoiceDetails } from '../../components/invoices/invoices.hook'
import { queryClient } from '../../context/constants'
import { axiosInstance } from '../../services/api'
import { getSHCodeByProp } from '../../services/userList'
import { convertDateYYYYMMDD, handleGlobalError } from '../../utils'
import { useAuthHook } from '../../utils/hooks/auth.hooks'
import { useGetCustAddr } from '../../utils/hooks/podfile.hook'
import { useGetDefautLocation } from '../../utils/hooks/shipto.hooks'
import { useSentryQuery } from '../../utils/useSentryQuery'
import { DashboardPageSchemaValidator } from '../../utils/validators/schemas'
import {
    DashboardData,
    InstallsInvData,
    InstallsOrdData,
    Unpacked,
} from './types'
/**
 * {
    "BRANCH": "1",
    "IVC_INVN": "114050",
    "IVC_ORDNO": "115310",
    "IVC_DATE": "04252022",
    "AMOUNT": "000563840+",
    "R_NUMBR": "114050      ",
    "R_TNET": "030",
    "R_TDAYS": "000",
    "R_TERMS": "00000",
    "BALANCE": "000563840+",
    "DMH_INSTALLDATE": "00000000",
    "DMH_ORDEREDBY": "JILL",
    "DMH-SHCODE": "0000"
}
 */
export const useGetDashboard = ({
    property,
}: {
    property?: Unpacked<
        ReturnType<typeof useGetCustomerAddress>['shipToItems']
    >['value']
} = {}) => {
    const { data: { userDetails } = {} } = useAuthHook()
    return useSentryQuery<DashboardData[]>({
        queryKey: ['getdashapivc', property],
        enabled: Boolean(property),
        queryFn: async ({ signal }) => {
            const resp = await axiosInstance.get('/api/getdashapivc', {
                params: {
                    function: 'getdashapivc',
                    alias: userDetails.api_config,
                    branch: userDetails?.Branches || userDetails.Branch,
                    company: userDetails.Company,
                    properties: property || userDetails.Properties,
                },
                signal,
            })
            return resp.data.ERROR ? [] : resp.data
        },
        staleTime: 60000, // 1 minute
    })
}
/**
 * {
    "BRANCH": "1 ",
    "DMO_ORDNO": "115179",
    "DMH_INSTALLDATE": "02112022"
}
 */

export const useGetInstallsOrd = ({
    startEndDate,
    properties,
    isEnabled = true,
}: {
    startEndDate: DashboardPageSchemaValidator['startEndDate']
    properties?: Unpacked<
        ReturnType<typeof useGetCustomerAddress>['shipToItems']
    >['value']
    isEnabled?: boolean
}) => {
    const { data: { userDetails } = {} } = useAuthHook()
    return useSentryQuery<InstallsOrdData[]>({
        queryKey: ['getinstallsord', startEndDate, properties],
        enabled: Boolean(properties) && isEnabled,
        queryFn: async ({ signal }) => {
            const resp = await axiosInstance.get('/api/getinstallsord_portal', {
                params: {
                    function: 'getinstallsord',
                    alias: userDetails.api_config,
                    branch: userDetails?.Branches || userDetails.Branch,
                    company: userDetails.Company,
                    properties: properties || userDetails.Properties,
                    startdate: convertDateYYYYMMDD(startEndDate.from),
                    enddate: convertDateYYYYMMDD(startEndDate.to),
                },
                signal,
            })
            return resp.data.ERROR ? [] : resp.data
        },
        staleTime: 60000, // 1 minute
    })
}
/**
 * {
    "BRANCH": "1 ",
    "IVC_CUSTOMER": "MANDALAY",
    "IVC_INVNO": "114097",
    "IVC_INSTALLDATE": "09222022"
}
 */

export const useGetInstallsInv = ({
    startEndDate,
    properties,
    isEnabled = true,
}: {
    startEndDate: DashboardPageSchemaValidator['startEndDate']
    properties?: Unpacked<
        ReturnType<typeof useGetCustomerAddress>['shipToItems']
    >['value']
    isEnabled?: boolean
}) => {
    const { data: { userDetails } = {} } = useAuthHook()
    return useSentryQuery<InstallsInvData[]>({
        queryKey: ['getinstallsinv', startEndDate, properties],
        enabled: Boolean(properties) && isEnabled,
        queryFn: async ({ signal }) => {
            const resp = await axiosInstance.get('/api/getinstallsinv_portal', {
                params: {
                    function: 'getinstallsinv_portal',
                    alias: userDetails.api_config,
                    branch: userDetails?.Branches || userDetails.Branch,
                    company: userDetails.Company,
                    properties: properties || userDetails.Properties,
                    startdate: convertDateYYYYMMDD(startEndDate.from),
                    enddate: convertDateYYYYMMDD(startEndDate.to),
                },
                signal,
            })
            return resp.data.ERROR ? [] : resp.data
        },
        staleTime: 60000, // 1 minute
    })
}
// {
//     "CADD_COID": "99",
//     "CADD_CUST": "MANDALAY",
//     "CADD_ADDRCD": "0000",
//     "CADD_NAME": "MANDALAY BAY",
//     "CADD_ADDR1": "3950 S LAS VEGAS BLVD",
//     "CADD_ADDR2": "                       #",
//     "CADD_CTYST": "LAS VEGAS, NV",
//     "CADD_ZIPCD": "89119-",
//     "CADD_PHONE1": "952-697-2505",
//     "CADD_PHONE2": "",
//     "CADD_FAX": "",
//     "CADD_CONTACT": "PATRICK",
//     "CADD_WEB_PO": "Y",
//     "CADD_WEB_BUILDING": "N",
//     "CADD_WEB_UNIT": "Y"
// }

export const useGetCustomerAddress = ({
    isEnabled = true,
    ...params
}: Parameters<typeof useGetCustAddr>[0] & {
    isEnabled: boolean
}) => {
    const { data, isLoading, error, ...rest } = useGetCustAddr(params)
    const { data: userData } = useAuthHook()
    const { data: defaultSelectedLocation } = useGetDefautLocation()
    const shipToItems = useMemo(() => {
        if (!data?.length) {
            return []
        }
        return data?.map((customer) => ({
            id: `${customer.CADD_CUST},${customer.CADD_ADDRCD}`,
            label: `${customer.CADD_CUST} - ${customer.CADD_NAME} (${customer.CADD_ADDR1})`,
            value: `${customer.CADD_CUST},${customer.CADD_ADDRCD}`,
        }))
    }, [data])

    if (isEnabled && !isLoading && shipToItems?.length === 0) {
        console.log('error===', error)
        handleGlobalError({ error, userData })
    }
    /**
     * Saves an item in local storage under the key 'defaultSelectedLocation'.
     * If the item already exists, it moves it to the beginning of the list.
     *
     * @param {string} item - The item to be saved in local storage.
     */
    const saveInLocalStorage = (item: string) => {
        localStorage.setItem('defaultSelectedLocation', item)
        queryClient.invalidateQueries({
            queryKey: ['getDefaultLocation', userData?.userDetails?.id],
        })
    }

    const defaultSelectedLocationValues: number[] = useMemo(() => {
        if (defaultSelectedLocation) {
            return shipToItems
                .map((item, index) => [item.value, index])
                .filter(([value]) => defaultSelectedLocation === value)
                .map(([, index]) => index) as number[]
        } else if (shipToItems?.[0]?.value) {
            saveInLocalStorage(shipToItems[0].value)
        }
        return []
    }, [shipToItems, defaultSelectedLocation])

    return {
        shipToItems,
        data,
        isLoading,
        ...rest,
        defaultSelectedLocationValues,
        saveInLocalStorage,
    }
}

export const useGetPropertiesByCustomer = (
    params: Omit<Parameters<typeof useGetCustAddr>[0], 'properties'> &
        Parameters<typeof getSHCodeByProp>[0]
) => {
    const { data: shCodes, isLoading: isShCodesLoading } = useSentryQuery({
        queryKey: ['getpropertiesbycustomer', params],
        queryFn: async ({ signal }) =>
            await getSHCodeByProp({ params, signal }),
        staleTime: Infinity, // 1 minute
        enabled: Boolean(params.customer) && Boolean(params.company),
    })

    const {
        shipToItems: properties,
        isLoading: isPropertiesLoading,
        defaultSelectedLocationValues,
    } = useGetCustomerAddress({
        branch: params.branch,
        company: params.company,
        properties: shCodes?.reduce((prevVal, currentVal) => {
            prevVal += `${currentVal.CUST},${currentVal.SHCODE}/*\\`
            return prevVal
        }, ''),
        isEnabled:
            Boolean(params.customer) &&
            Boolean(params.company) &&
            Boolean(shCodes?.length),
    })

    return {
        shCodes,
        properties: properties.length
            ? [{ id: 'all', label: 'Select All', value: 'all' }, ...properties]
            : [],
        defaultSelectedLocationValues,
        isLoading: isPropertiesLoading || isShCodesLoading,
    }
}

export const getSubTotal = (
    invoice: Parameters<typeof useGetInvoiceDetails>[0]['invoiceRow']
) => {
    const ivcFees = Number(parseStringToDecimal(invoice?.IVC_FEES))
    const material = Number(parseStringToDecimal(invoice?.IVC_MATERIAL))
    const labor = Number(parseStringToDecimal(invoice?.IVC_LABOR))
    const specialChanges = Number(parseStringToDecimal(invoice?.IVC_SPECIAL))

    return (ivcFees + material + labor + specialChanges).toFixed(2)
}

export const getGrandTotal = (
    invoice: Parameters<typeof useGetInvoiceDetails>[0]['invoiceRow']
) => {
    const subTotal = Number(getSubTotal(invoice))
    const totalTax = Number(parseStringToDecimal(invoice?.IVC_TOTTAX))
    return (subTotal + totalTax).toFixed(2)
}

export const parseStringToDecimal = (value: string): string => {
    if (!value) return '0.00'

    const sign = value.endsWith('+') ? 1 : value.endsWith('-') ? -1 : 0
    const numericPart = value.slice(0, -1).replace(/^0+/, '') // Remove leading zeros
    const decimalValue = (Number(numericPart) / 100) * sign

    return decimalValue.toFixed(2) // Format to 2 decimal places
}

export const getBalance = (
    invoice: Parameters<typeof useGetInvoiceDetails>[0]['invoiceRow']
) => {
    const grandTotal = Number(getGrandTotal(invoice))
    const paid = Number(parseStringToDecimal(invoice?.DEP_AMOUNT))
    return grandTotal - paid
}
